import Resource from 'rest-resource'
import axios from 'axios'
import router from '../router'
import { parse as urlParse } from 'url'
import { parse as queryParse } from 'querystring'
import { BaseClient } from 'rest-resource/dist/client'
import { range, flatten } from 'lodash'
import { timeDateFormattedPretty } from '@/util'
import assert from 'assert'
import { env } from '../config'

export function getTokenPayload(token) {
    try {
        let jwtPieces = token.split('.')
        let payloadBase64 = jwtPieces[1]
        let payloadBuffer = Buffer.from(payloadBase64, 'base64').toString()
        return JSON.parse(payloadBuffer.toString())
    } catch (e) {
        return undefined
    }
}

export class Client extends BaseClient {
    constructor(baseURL, token, options = {}) {
        if (!options.headers) {
            options.headers = {}
        }

        if (options.locationGroupName) {
            options.headers['X-ShiftOS-Location-Group'] = options.locationGroupName
        }

        super(baseURL, options)
        
        if (token) {
            this.token = token
        }
    }

    negotiateContent(ResourceClass) {
        return (response) => {
            if (PagedContentNegotiator.isListResponse(response)) {
                return new PagedContentNegotiator(ResourceClass, response)
            } else {
                return new DefaultContentNegotiator(ResourceClass, response)
            }
        }
    }

    get token() {
        return this._token
    }

    set token(value) {
        if (!this.config.headers) {
            this.config.headers = {}
        }

        this._token = value
        
        if (this._token) {
            this.config.headers['Authorization'] = `Bearer ${value}`
        }

        this.axios = axios.create(this.config)
    }

    getTokenPayload() {
        return getTokenPayload(this.token)
    }

    tokenIsExpired() {
        try {
            let payload = this.getTokenPayload()
            let nowInSeconds = Math.floor(Date.now() / 1000)
            return payload.exp < nowInSeconds
        } catch (e) {
            return true
        }
    }

    tokenIsValid() {
        return this.token && !this.tokenIsExpired()
    }

    tokenBecomesExpired() {
        router.push({ name: 'login', query: { errorCode: 'tokenExpired' } })
    }

    onError(e) {
        if(e.response?.data?.code == 'token_not_valid') {
            this.tokenBecomesExpired()
        }
        
        throw e
    }
}

export class DefaultContentNegotiator {
    resources = []

    constructor(ResourceClass, response) {
        this.ResourceClass = ResourceClass
        this.response = response
        this.body = this.response.data
        this.urlInfo = urlParse(response.config.url)

        if ('string' === typeof this.urlInfo.query) {
            this.query = queryParse(this.urlInfo.query) || {}
        } else {
            this.query = {}
        }

        this.parseResources()
    }

    parseResources() {
        const ResourceClass = this.ResourceClass
        this.resources.push(new ResourceClass(this.body))
    }
}

export class PagedContentNegotiator extends DefaultContentNegotiator {
    constructor(ResourceClass, response) {
        super(ResourceClass, response)
        assert(this.body.results, 'Can\'t create PageResponse instance without "results" property in body')
        this.count = Number(this.body.count)
        this.pages = Number(this.body.page_count)
        this.pageSize = Number(this.body.page_size)
        this.page = Number(this.query.page) || 1
    }

    static isListResponse(response) {
        return response.data && response.data.results
    }

    assertPageSize(page) {
        assert(page >= 1 && page <= this.pages, `Page size must be between 1 and ${this.pages} (received ${page})`)
    }

    parseResources() {
        const ResourceClass = this.ResourceClass
        for (let i = 0; i < this.body.results.length; i++) {
            this.resources.push(new ResourceClass(this.body.results[i]))
        }
    }

    nextPage() {
        const ResourceClass = this.ResourceClass
        const query = Object.assign({}, this.query, { page: this.page + 1 })
        this.assertPageSize(query.page)
        return ResourceClass.list({ query })
    }

    prevPage() {
        const ResourceClass = this.ResourceClass
        const query = Object.assign({}, this.query, { page: this.page - 1 })
        this.assertPageSize(query.page)
        return ResourceClass.list({ query })
    }

    otherPageNumbers() {
        return range(1, this.pages + 1, 1).filter((page) => page != this.page)
    }
}

export const client = new Client(env.API_BASE_URL, null, {
    locationGroupName: env.API_LOCATION_GROUP_ID || null
})

if (localStorage.getItem('token')) {
    client.token = localStorage.getItem('token')
}

export default class BaseResource extends Resource {
    static client = client

    constructor(...args) {
        super(...args)
        Object.assign(this._attributes, this._attributes.readonly_values)
    }

    static async all(options = {}) {
        let content = await this.list(options)
        let otherPages = content.otherPageNumbers()

        let allResources = {
            [content.page]: content.resources,
        }

        let promises = otherPages.map((page) => {
            let thisPageQuery = Object.assign({}, content.query, { page })
            return this.list(Object.assign({}, options, { query: thisPageQuery })).then((res) => {
                allResources[page] = res.resources
            })
        })

        await Promise.all(promises)
        return flatten(Object.values(allResources))
    }

    static filter(filterArgs) {
        return this.list({ query: filterArgs })
    }

    static filterAll(filterArgs, options = {}) {
        let opts = Object.assign({}, { query: filterArgs }, options)
        return this.all(opts)
    }

    static search(searchString, filterArgs = {}) {
        let query = Object.assign(filterArgs, { search: searchString })
        return this.list({ query })
    }

    static searchAll(searchString, filterArgs = {}) {
        let query = Object.assign(filterArgs, { search: searchString })
        return this.all({ query })
    }

    createdPretty() {
        if (!this.attributes.created) {
            return 'Unknown'
        }

        return timeDateFormattedPretty(this.attributes.created)
    }

    toString() {
        if (this.attributes.name) {
            return this.attributes.name
        }

        return super.toString()
    }
}
